package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.external.Sortable
import com.castly.admin.external.createSortableOptions
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.BulkCreateEpisodesRequest
import com.castly.shared.dto.BulkEpisodeItem
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import com.castly.admin.ui.friendlyUploadError
import org.w3c.files.File
import org.w3c.files.FileList
import org.w3c.files.get

enum class UploadStatus { PENDING, UPLOADING, COMPLETE, FAILED }

data class StagingEpisode(
    val id: String,
    val file: File,
    var title: String,
    var displayOrder: Int,
    val fileSize: Double,
    var uploadProgress: Float = 0f,
    var uploadStatus: UploadStatus = UploadStatus.PENDING,
    var audioUrl: String? = null,
    var durationSeconds: Int? = null,
    var errorMessage: String? = null
)

@Composable
fun BulkEpisodeUploadPanel(
    audiobookId: String,
    apiClient: ApiClient,
    onComplete: () -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var stagingEpisodes by remember { mutableStateOf<List<StagingEpisode>>(emptyList()) }
    var isSaving by remember { mutableStateOf(false) }
    var saveError by remember { mutableStateOf<String?>(null) }
    var warningMessage by remember { mutableStateOf<String?>(null) }
    var listContainer by remember { mutableStateOf<HTMLElement?>(null) }

    val allUploadsComplete = stagingEpisodes.isNotEmpty() && stagingEpisodes.all { it.uploadStatus == UploadStatus.COMPLETE }
    val hasFailedUploads = stagingEpisodes.any { it.uploadStatus == UploadStatus.FAILED }

    // SortableJS setup
    DisposableEffect(listContainer) {
        val container = listContainer
        if (container != null && stagingEpisodes.isNotEmpty()) {
            val sortable = Sortable.create(container, createSortableOptions {
                animation = 150
                handle = ".drag-handle"
                ghostClass = "sortable-ghost"
                delay = 100
                delayOnTouchOnly = true
                onEnd = { event ->
                    val oldIdx = event.oldIndex
                    val newIdx = event.newIndex
                    if (oldIdx != null && newIdx != null && oldIdx != newIdx) {
                        val mutable = stagingEpisodes.toMutableList()
                        val item = mutable.removeAt(oldIdx)
                        mutable.add(newIdx, item)
                        mutable.forEachIndexed { index, ep -> ep.displayOrder = index }
                        stagingEpisodes = mutable.toList()
                    }
                }
            })
            onDispose { sortable.destroy() }
        } else {
            onDispose { }
        }
    }

    fun handleFileSelection(files: FileList?) {
        if (files == null || files.length == 0) return

        val validFiles = mutableListOf<File>()
        val skippedFiles = mutableListOf<String>()
        val oversizedFiles = mutableListOf<String>()

        for (i in 0 until files.length) {
            val file = files[i] ?: continue
            val name = file.name.lowercase()
            if (!name.endsWith(".mp3")) {
                skippedFiles.add(file.name)
                continue
            }
            if (file.size.toDouble() > 500.0 * 1024 * 1024) {
                oversizedFiles.add(file.name)
                continue
            }
            validFiles.add(file)
        }

        val warnings = mutableListOf<String>()
        if (skippedFiles.isNotEmpty()) {
            warnings.add("Skipped non-MP3 files: ${skippedFiles.joinToString(", ")}")
        }
        if (oversizedFiles.isNotEmpty()) {
            warnings.add("Skipped files over 500MB: ${oversizedFiles.joinToString(", ")}")
        }
        warningMessage = warnings.joinToString(". ").takeIf { it.isNotBlank() }

        if (validFiles.isEmpty()) {
            if (stagingEpisodes.isEmpty()) {
                saveError = "No valid MP3 files found."
            }
            return
        }

        val startOrder = stagingEpisodes.size
        val newEntries = validFiles.mapIndexed { index, file ->
            StagingEpisode(
                id = kotlin.random.Random.nextInt(100000, 999999).toString(),
                file = file,
                title = file.name.substringBeforeLast("."),
                displayOrder = startOrder + index,
                fileSize = file.size.toDouble()
            )
        }

        stagingEpisodes = stagingEpisodes + newEntries

        // Start uploading each file immediately
        newEntries.forEach { episode ->
            scope.launch {
                val idx = stagingEpisodes.indexOfFirst { it.id == episode.id }
                if (idx < 0) return@launch

                val updated = stagingEpisodes.toMutableList()
                updated[idx] = updated[idx].copy(uploadStatus = UploadStatus.UPLOADING, uploadProgress = 0f)
                stagingEpisodes = updated.toList()

                try {
                    val result = apiClient.uploadEpisodeFile(audiobookId, episode.file) { fraction ->
                        val progressList = stagingEpisodes.toMutableList()
                        val pi = progressList.indexOfFirst { it.id == episode.id }
                        if (pi >= 0) {
                            progressList[pi] = progressList[pi].copy(uploadProgress = fraction)
                            stagingEpisodes = progressList.toList()
                        }
                    }
                    val updated2 = stagingEpisodes.toMutableList()
                    val idx2 = updated2.indexOfFirst { it.id == episode.id }
                    if (idx2 >= 0) {
                        updated2[idx2] = updated2[idx2].copy(
                            uploadStatus = UploadStatus.COMPLETE,
                            uploadProgress = 1f,
                            audioUrl = result.audioUrl,
                            durationSeconds = result.durationSeconds
                        )
                        stagingEpisodes = updated2.toList()
                    }
                } catch (e: Exception) {
                    val updated2 = stagingEpisodes.toMutableList()
                    val idx2 = updated2.indexOfFirst { it.id == episode.id }
                    if (idx2 >= 0) {
                        updated2[idx2] = updated2[idx2].copy(
                            uploadStatus = UploadStatus.FAILED,
                            uploadProgress = 0f,
                            errorMessage = friendlyUploadError(e.message)
                        )
                        stagingEpisodes = updated2.toList()
                    }
                }
            }
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            themeTransition()
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.lg)
            }
        }) {
            H3({
                style {
                    margin(0.px)
                    fontSize(Typography.lg)
                    fontWeight("600")
                    color(Color(colors.text))
                }
            }) {
                Text("Add Episodes")
            }

            Button({
                onClick { onCancel() }
                style {
                    padding(6.px, 12.px)
                    backgroundColor(Color("transparent"))
                    border(1.px, LineStyle.Solid, Color(colors.border))
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    color(Color(colors.text))
                }
            }) {
                Text("Close")
            }
        }

        // File input
        Div({
            style {
                marginBottom(Spacing.lg)
            }
        }) {
            Input(InputType.File) {
                attr("accept", ".mp3,audio/mpeg")
                attr("multiple", "true")
                onChange { event ->
                    val input = event.nativeEvent.target.asDynamic()
                    val files = input.files.unsafeCast<FileList?>()
                    handleFileSelection(files)
                    // Reset input so same files can be selected again
                    input.value = ""
                }
                style {
                    display(DisplayStyle.None)
                }
                id("bulk-episode-file-input")
            }

            Button({
                onClick {
                    kotlinx.browser.document.getElementById("bulk-episode-file-input")?.asDynamic()?.click()
                }
                style {
                    padding(10.px, Spacing.xl)
                    backgroundColor(Color(colors.primary))
                    color(Color("#ffffff"))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    fontSize(Typography.base)
                    fontWeight("500")
                }
            }) {
                Text("Select MP3 Files")
            }
        }

        // Warning message
        warningMessage?.let { msg ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color("#fef3c7"))
                    border(1.px, LineStyle.Solid, Color("#f59e0b"))
                    borderRadius(BorderRadius.md)
                    marginBottom(Spacing.lg)
                    color(Color("#92400e"))
                    fontSize(Typography.sm)
                }
            }) {
                Text(msg)
            }
        }

        // Error message
        saveError?.let { err ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color("#fee2e2"))
                    border(1.px, LineStyle.Solid, Color("#ef4444"))
                    borderRadius(BorderRadius.md)
                    marginBottom(Spacing.lg)
                    color(Color("#dc2626"))
                    fontSize(Typography.sm)
                }
            }) {
                Text(err)
            }
        }

        // Staging table
        if (stagingEpisodes.isNotEmpty()) {
            Div({
                ref { element ->
                    listContainer = element
                    onDispose { listContainer = null }
                }
                style {
                    marginBottom(Spacing.lg)
                }
            }) {
                stagingEpisodes.forEachIndexed { index, episode ->
                    Div({
                        attr("data-episode-id", episode.id)
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.md)
                            padding(Spacing.md)
                            backgroundColor(Color(if (index % 2 == 0) colors.surface else colors.surfaceHover))
                            borderRadius(BorderRadius.md)
                            marginBottom(4.px)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                        }
                    }) {
                        // Drag handle
                        Div({
                            classes("drag-handle")
                            style {
                                cursor("grab")
                                color(Color(colors.textMuted))
                                fontSize(Typography.lg)
                                property("user-select", "none")
                                width(24.px)
                                textAlign("center")
                            }
                        }) {
                            Text("\u2261") // hamburger icon
                        }

                        // Order number
                        Div({
                            style {
                                width(30.px)
                                textAlign("center")
                                fontSize(Typography.sm)
                                color(Color(colors.textMuted))
                                fontWeight("600")
                            }
                        }) {
                            Text("${episode.displayOrder + 1}")
                        }

                        // Editable title
                        Input(InputType.Text) {
                            value(episode.title)
                            onInput { event ->
                                val newTitle = event.value
                                val updated = stagingEpisodes.toMutableList()
                                val idx = updated.indexOfFirst { it.id == episode.id }
                                if (idx >= 0) {
                                    updated[idx] = updated[idx].copy(title = newTitle)
                                    stagingEpisodes = updated.toList()
                                }
                            }
                            style {
                                flex(1)
                                padding(6.px, 10.px)
                                fontSize(Typography.sm)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.sm)
                                property("outline", "none")
                                property("box-sizing", "border-box")
                                backgroundColor(Color(colors.surface))
                                color(Color(colors.text))
                            }
                        }

                        // File size
                        Div({
                            style {
                                width(70.px)
                                textAlign("right")
                                fontSize(Typography.xs)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            val sizeMB = episode.fileSize / (1024 * 1024)
                            Text("${sizeMB.asDynamic().toFixed(1)} MB")
                        }

                        // Duration (if available)
                        Div({
                            style {
                                width(50.px)
                                textAlign("right")
                                fontSize(Typography.xs)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            episode.durationSeconds?.let { sec ->
                                val mins = sec / 60
                                val secs = sec % 60
                                Text("${mins}:${secs.toString().padStart(2, '0')}")
                            }
                        }

                        // Upload status
                        Div({
                            style {
                                width(80.px)
                            }
                        }) {
                            when (episode.uploadStatus) {
                                UploadStatus.PENDING -> {
                                    Div({
                                        style {
                                            fontSize(Typography.xs)
                                            color(Color(colors.textMuted))
                                        }
                                    }) { Text("Pending") }
                                }
                                UploadStatus.UPLOADING -> {
                                    val pct = (episode.uploadProgress * 100).toInt()
                                    Div({
                                        style {
                                            height(6.px)
                                            backgroundColor(Color(colors.border))
                                            borderRadius(3.px)
                                            property("overflow", "hidden")
                                        }
                                    }) {
                                        Div({
                                            style {
                                                height(100.percent)
                                                property("width", "${pct}%")
                                                backgroundColor(Color(colors.primary))
                                                borderRadius(3.px)
                                                property("transition", "width 0.2s ease")
                                            }
                                        })
                                    }
                                    Div({
                                        style {
                                            fontSize(Typography.xs)
                                            color(Color(colors.textMuted))
                                            marginTop(2.px)
                                        }
                                    }) { Text("${pct}%") }
                                }
                                UploadStatus.COMPLETE -> {
                                    Div({
                                        style {
                                            fontSize(Typography.xs)
                                            color(Color(colors.success))
                                            fontWeight("600")
                                        }
                                    }) { Text("Done") }
                                }
                                UploadStatus.FAILED -> {
                                    Div({
                                        style {
                                            fontSize(Typography.xs)
                                            color(Color(colors.danger))
                                            fontWeight("600")
                                        }
                                    }) {
                                        Text("Failed")
                                        episode.errorMessage?.let { err ->
                                            Br()
                                            Span({
                                                style {
                                                    fontWeight("400")
                                                    fontSize(11.px)
                                                }
                                            }) { Text(err) }
                                        }
                                    }
                                }
                            }
                        }

                        // Remove button
                        Button({
                            onClick {
                                val updated = stagingEpisodes.toMutableList()
                                updated.removeAll { it.id == episode.id }
                                updated.forEachIndexed { i, ep -> ep.displayOrder = i }
                                stagingEpisodes = updated.toList()
                            }
                            style {
                                padding(4.px, 8.px)
                                backgroundColor(Color("transparent"))
                                border(0.px)
                                cursor("pointer")
                                color(Color(colors.danger))
                                fontSize(Typography.sm)
                            }
                        }) {
                            Text("\u00d7")
                        }
                    }
                }
            }

            // Summary and Save button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.sm)
                        color(Color(colors.textMuted))
                    }
                }) {
                    val completeCount = stagingEpisodes.count { it.uploadStatus == UploadStatus.COMPLETE }
                    Text("${completeCount}/${stagingEpisodes.size} files uploaded")
                    if (hasFailedUploads) {
                        Text(" (${stagingEpisodes.count { it.uploadStatus == UploadStatus.FAILED }} failed)")
                    }
                }

                Button({
                    onClick {
                        scope.launch {
                            isSaving = true
                            saveError = null
                            try {
                                val items = stagingEpisodes
                                    .filter { it.uploadStatus == UploadStatus.COMPLETE && it.audioUrl != null }
                                    .sortedBy { it.displayOrder }
                                    .mapIndexed { index, ep ->
                                        BulkEpisodeItem(
                                            title = ep.title,
                                            audioUrl = ep.audioUrl!!,
                                            displayOrder = index,
                                            fileSizeBytes = ep.fileSize.toLong(),
                                            durationSeconds = ep.durationSeconds
                                        )
                                    }

                                if (items.isEmpty()) {
                                    saveError = "No successfully uploaded files to save."
                                    return@launch
                                }

                                apiClient.bulkCreateEpisodes(audiobookId, BulkCreateEpisodesRequest(items))
                                onComplete()
                            } catch (e: Exception) {
                                saveError = e.message ?: "Failed to create episodes"
                            } finally {
                                isSaving = false
                            }
                        }
                    }
                    if (!allUploadsComplete || isSaving) disabled()
                    style {
                        padding(10.px, Spacing.xl)
                        backgroundColor(if (allUploadsComplete && !isSaving) Color(colors.primary) else Color(colors.textMuted))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor(if (allUploadsComplete && !isSaving) "pointer" else "not-allowed")
                        fontSize(Typography.base)
                        fontWeight("500")
                    }
                }) {
                    Text(if (isSaving) "Saving..." else "Save Episodes")
                }
            }
        }
    }
}
